import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import { SplitAuthLayout } from "./Login";

const Register = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    agent_number: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/agent/signup", {
        ...form,
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
      });
      setSuccess(true);
      toast.success(t("toast_signup_success"));
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      setError(detail || t("err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <SplitAuthLayout>
        <div className="space-y-6 animate-fade-in" data-testid="register-success">
          <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="space-y-2">
            <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
              {t("register_success_title")}
            </h2>
            <p className="text-slate-500 leading-relaxed">{t("register_success_body")}</p>
          </div>
          <Button
            onClick={() => navigate("/login")}
            className="bg-[#0A2540] hover:bg-[#051626] text-white"
            data-testid="register-success-back-button"
          >
            {t("auth_callback_back")}
          </Button>
        </div>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout>
      <div className="space-y-2 mb-7">
        <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
          {t("register_heading")}
        </h2>
        <p className="text-slate-500 text-sm">{t("register_subheading")}</p>
      </div>

      <form className="space-y-4" onSubmit={onSubmit} data-testid="register-form">
        <div className="space-y-1.5">
          <Label htmlFor="r-name">{t("register_name")}</Label>
          <Input
            id="r-name"
            required
            value={form.name}
            onChange={onChange("name")}
            data-testid="register-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="r-email">{t("register_email")}</Label>
          <Input
            id="r-email"
            type="email"
            required
            value={form.email}
            onChange={onChange("email")}
            data-testid="register-email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="r-password">{t("register_password")}</Label>
          <Input
            id="r-password"
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={onChange("password")}
            data-testid="register-password-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="r-agent">{t("register_agent_number")}</Label>
            <Input
              id="r-agent"
              required
              value={form.agent_number}
              onChange={onChange("agent_number")}
              data-testid="register-agent-number-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-phone">{t("register_phone")}</Label>
            <Input
              id="r-phone"
              required
              value={form.phone}
              onChange={onChange("phone")}
              data-testid="register-phone-input"
            />
          </div>
        </div>

        {error ? (
          <div
            className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2"
            data-testid="register-error"
          >
            {error}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-11 bg-[#0A2540] hover:bg-[#051626] text-white font-medium"
          data-testid="register-submit-button"
        >
          {submitting ? "..." : t("register_submit")}
        </Button>

        <p className="text-sm text-slate-500 text-center">
          <Link to="/login" className="text-[#007AFF] hover:underline" data-testid="link-to-login">
            {t("register_to_login")}
          </Link>
        </p>
      </form>
    </SplitAuthLayout>
  );
};

export default Register;
